import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, Tag, Warning, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Inventory = () => {
  const navigate = useNavigate();
  const { products, categories, refreshProducts } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [movements, setMovements] = useState([]);
  const [view, setView] = useState('products'); // 'products' | 'movements' | 'no-movement'
  const [noMovement, setNoMovement] = useState([]);
  const [noMovementDays, setNoMovementDays] = useState(30);
  const [noMovementLoading, setNoMovementLoading] = useState(false);
  const [movSearch, setMovSearch] = useState('');
  const [movFrom, setMovFrom] = useState('');
  const [movTo, setMovTo] = useState('');
  const [saleDetail, setSaleDetail] = useState({ open: false, loading: false, sale: null, saleId: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStock, setFilterStock] = useState('all'); // all | low | out | with_stock
  const [filterPromo, setFilterPromo] = useState('all'); // all | with | without
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ product_id: '', quantity: '', type: 'entrada', notes: '' });

  // Promo dialog (per-product / per-variant)
  const [promoDialog, setPromoDialog] = useState({ open: false, product: null });
  const [promoForm, setPromoForm] = useState({
    has_promo: false,
    promo_type: 'flat',
    promo_price: '',
    promo_min_qty: '',
    promo_end_date: ''
  });
  const [pendingApprovalId, setPendingApprovalId] = useState(null);

  useEffect(() => {
    fetchMovements();
  }, []);

  // polling para aprobaciones de promo
  useEffect(() => {
    if (!pendingApprovalId) return;
    const i = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/approvals/${pendingApprovalId}`, { withCredentials: true });
        if (data.status === 'approved') {
          toast.success('✅ Promoción aprobada y aplicada');
          setPendingApprovalId(null);
          refreshProducts();
        } else if (data.status === 'rejected') {
          toast.error('❌ Promoción rechazada');
          setPendingApprovalId(null);
        }
      } catch (e) { /* ignore */ }
    }, 2500);
    return () => clearInterval(i);
  }, [pendingApprovalId, refreshProducts]);

  const fetchMovements = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/movements`, { withCredentials: true });
      setMovements(data);
    } catch (e) { /* ignore */ }
  };

  // Extrae el sale_id desde el campo notes ("Venta xxxxxx ...")
  const extractSaleId = (notes) => {
    if (!notes) return null;
    const m = String(notes).match(/Venta\s+([a-f0-9]{20,32})/i);
    return m ? m[1] : null;
  };

  const openSaleDetail = async (saleId) => {
    if (!saleId) return;
    setSaleDetail({ open: true, loading: true, sale: null, saleId });
    try {
      const { data } = await axios.get(`${API}/sales/${saleId}`, { withCredentials: true });
      setSaleDetail({ open: true, loading: false, sale: data, saleId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la venta');
      setSaleDetail({ open: false, loading: false, sale: null, saleId: null });
    }
  };

  const filteredMovements = (() => {
    const q = movSearch.trim().toLowerCase();
    const fromIso = movFrom ? new Date(movFrom + 'T00:00:00').toISOString() : null;
    // end-of-day inclusive
    const toIso = movTo ? new Date(movTo + 'T23:59:59.999').toISOString() : null;
    return movements.filter(m => {
      if (q && !((m.product_name || '').toLowerCase().includes(q) || (m.notes || '').toLowerCase().includes(q))) return false;
      const ca = m.created_at;
      if (fromIso && ca < fromIso) return false;
      if (toIso && ca > toIso) return false;
      return true;
    });
  })();

  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      const adjustmentData = { ...adjustForm, quantity: parseInt(adjustForm.quantity) };
      await axios.post(`${API}/inventory/adjustment`, adjustmentData, { withCredentials: true });
      toast.success('Ajuste registrado');
      setShowAdjustDialog(false);
      setAdjustForm({ product_id: '', quantity: '', type: 'entrada', notes: '' });
      fetchMovements();
      refreshProducts();
    } catch (error) {
      toast.error('Error al registrar ajuste');
    }
  };

  // ----- Filtros -----
  const hiddenParentIds = useMemo(() => {
    const parentIds = new Set(products.map(p => p.parent_product_id).filter(Boolean));
    return new Set(
      products
        .filter(p => (p.has_variants || parentIds.has(p.id)) && p.visible_in_pos === false)
        .map(p => p.id)
    );
  }, [products]);

  const filteredProducts = products.filter((p) => {
    // Ocultos del sistema: no permitir acciones
    if (p.visible_in_pos === false) return false;
    // Variantes cuyo padre está oculto: también deben excluirse
    if (p.parent_product_id && hiddenParentIds.has(p.parent_product_id)) return false;
    const term = searchTerm.toLowerCase();
    if (term && !p.name.toLowerCase().includes(term) && !(p.sku || '').toLowerCase().includes(term)) {
      return false;
    }
    if (filterCategory !== 'all') {
      // incluye subcategorías
      const subIds = categories.filter((c) => c.parent_id === filterCategory).map((c) => c.id);
      const allowedIds = [filterCategory, ...subIds];
      if (!allowedIds.includes(p.category_id)) return false;
    }
    const stock = p.stock || 0;
    const minStock = p.min_stock || 10;
    if (filterStock === 'out' && stock > 0) return false;
    if (filterStock === 'low' && (stock <= 0 || stock > minStock)) return false;
    if (filterStock === 'with_stock' && stock <= 0) return false;
    const hasPromo = !!p.has_promo;
    if (filterPromo === 'with' && !hasPromo) return false;
    if (filterPromo === 'without' && hasPromo) return false;
    return true;
  });

  // ----- Promo dialog -----
  const openPromoDialog = (product) => {
    setPromoDialog({ open: true, product });
    setPromoForm({
      has_promo: !!product.has_promo,
      promo_type: product.promo_type || 'flat',
      promo_price: product.promo_price ?? '',
      promo_min_qty: product.promo_min_qty ?? '',
      promo_end_date: product.promo_end_date || ''
    });
  };

  const submitPromo = async () => {
    const product = promoDialog.product;
    if (!product) return;
    if (promoForm.has_promo && (promoForm.promo_price === '' || isNaN(parseFloat(promoForm.promo_price)))) {
      toast.error('Indica el precio de promoción');
      return;
    }
    if (promoForm.has_promo && promoForm.promo_type === 'min_qty' && !promoForm.promo_min_qty) {
      toast.error('Indica la cantidad mínima para aplicar la promo');
      return;
    }
    const payload = {
      product_id: product.id,
      has_promo: !!promoForm.has_promo,
      promo_type: promoForm.has_promo ? promoForm.promo_type : null,
      promo_price: promoForm.has_promo && promoForm.promo_price !== '' ? parseFloat(promoForm.promo_price) : null,
      promo_min_qty: promoForm.has_promo && promoForm.promo_type === 'min_qty' && promoForm.promo_min_qty !== ''
        ? parseInt(promoForm.promo_min_qty) : null,
      promo_end_date: promoForm.has_promo && promoForm.promo_end_date ? promoForm.promo_end_date : null
    };
    try {
      const { data } = await axios.post(`${API}/approvals/promo-change`, payload, { withCredentials: true });
      if (data.status === 'approved') {
        toast.success('Promoción actualizada');
        refreshProducts();
      } else {
        setPendingApprovalId(data.id);
        toast.info('⏳ Esperando aprobación del admin…');
      }
      setPromoDialog({ open: false, product: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // ----- Render helpers -----
  const productNameWithVariant = (p) => p.variant_name ? `${p.name} (${p.variant_name})` : p.name;

  const stockBadge = (p) => {
    const stock = p.stock || 0;
    const minStock = p.min_stock || 10;
    if (stock <= 0) return <span className="text-rose-600 font-semibold">Agotado</span>;
    if (stock <= minStock) return <span className="text-amber-600 font-semibold">{stock}</span>;
    return <span className="text-emerald-600 font-semibold">{stock}</span>;
  };

  const promoLabel = (p) => {
    if (!p.has_promo) return null;
    const expired = p.promo_end_date && new Date(p.promo_end_date + 'T23:59:59') < new Date();
    if (expired) return <span className="text-xs text-muted-foreground">vencida</span>;
    if (p.promo_type === 'flat') return <span className="text-xs">${p.promo_price?.toFixed(2)} fijo</span>;
    return <span className="text-xs">${p.promo_price?.toFixed(2)} (≥{p.promo_min_qty})</span>;
  };

  const lowStockCount = products.filter((p) => (p.stock || 0) > 0 && (p.stock || 0) <= (p.min_stock || 10)).length;
  const outOfStockCount = products.filter((p) => (p.stock || 0) <= 0).length;

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Inventario</h1>
            <p className="text-muted-foreground mt-1">Productos, stock, promociones y movimientos</p>
          </div>
          <div className="flex gap-2">
            <div className="bg-slate-100 p-1 rounded-full text-xs flex gap-1">
              <button onClick={() => setView('products')} data-testid="inv-tab-products"
                className={`px-3 py-1 rounded-full btn-transition ${view === 'products' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'}`}>
                Productos
              </button>
              <button onClick={() => setView('movements')} data-testid="inv-tab-movements"
                className={`px-3 py-1 rounded-full btn-transition ${view === 'movements' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'}`}>
                Movimientos
              </button>
              <button onClick={() => {
                  setView('no-movement');
                  if (noMovement.length === 0) {
                    setNoMovementLoading(true);
                    axios.get(`${API}/products/no-movement?days=${noMovementDays}`, { withCredentials: true })
                      .then(({ data }) => setNoMovement(data))
                      .catch(() => setNoMovement([]))
                      .finally(() => setNoMovementLoading(false));
                  }
                }}
                data-testid="inv-tab-no-movement"
                className={`px-3 py-1 rounded-full btn-transition ${view === 'no-movement' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'}`}>
                Sin movimiento
              </button>
            </div>
            <Button onClick={() => setShowAdjustDialog(true)} data-testid="add-adjustment-button" variant="outline">
              <Plus size={18} weight="bold" className="mr-1" />
              Ajuste manual
            </Button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border rounded-sm p-3">
            <p className="text-xs text-muted-foreground">Total productos</p>
            <p className="text-2xl font-bold">{products.length}</p>
          </div>
          <div className="bg-white border rounded-sm p-3">
            <p className="text-xs text-muted-foreground">Stock bajo</p>
            <p className="text-2xl font-bold text-amber-600">{lowStockCount}</p>
          </div>
          <div className="bg-white border rounded-sm p-3">
            <p className="text-xs text-muted-foreground">Agotados</p>
            <p className="text-2xl font-bold text-rose-600">{outOfStockCount}</p>
          </div>
        </div>

        {view === 'products' ? (
          <>
            {/* Filtros */}
            <div className="bg-white border border-border rounded-sm p-3 mb-3 flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[240px] relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre o SKU…"
                  className="pl-10"
                  data-testid="inv-search"
                />
              </div>
              <div className="min-w-[180px]">
                <Label className="text-xs">Categoría</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger data-testid="inv-filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.filter((c) => !c.parent_id).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-xs">Stock</Label>
                <Select value={filterStock} onValueChange={setFilterStock}>
                  <SelectTrigger data-testid="inv-filter-stock"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="with_stock">Con stock</SelectItem>
                    <SelectItem value="low">Stock bajo</SelectItem>
                    <SelectItem value="out">Agotados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-xs">Promoción</Label>
                <Select value={filterPromo} onValueChange={setFilterPromo}>
                  <SelectTrigger data-testid="inv-filter-promo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="with">Con promoción</SelectItem>
                    <SelectItem value="without">Sin promoción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lista */}
            <div className="bg-white border border-border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Producto</th>
                    <th className="px-3 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-left">Categoría</th>
                    <th className="px-3 py-2 text-right">Precio 1</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2 text-left">Promoción</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.slice(0, 500).map((p) => {
                    const cat = categories.find((c) => c.id === p.category_id);
                    return (
                      <tr key={p.id} className="hover:bg-slate-50" data-testid={`inv-row-${p.id}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {p.image_url && <img src={p.image_url} alt={p.name} className="w-8 h-8 object-cover rounded flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="font-medium truncate">{productNameWithVariant(p)}</p>
                              {p.brand && <p className="text-xs text-muted-foreground uppercase">{p.brand}</p>}
                            </div>
                            {p.parent_product_id && <span className="text-[10px] bg-slate-100 px-1 rounded flex-shrink-0">variante</span>}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="text-blue-500 hover:text-blue-700 flex-shrink-0" data-testid={`info-${p.id}`} title="Ver detalles">
                                  <Info size={16} weight="regular" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80" side="right">
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-center gap-2 pb-2 border-b">
                                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-12 h-12 object-cover rounded" />}
                                    <div>
                                      <p className="font-semibold">{productNameWithVariant(p)}</p>
                                      <p className="text-xs text-muted-foreground">SKU: {p.sku}</p>
                                      {p.brand && <p className="text-xs uppercase text-muted-foreground">Marca: {p.brand}</p>}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                    <div>Precio 1:</div><div className="text-right font-mono">${(p.precio_1 || 0).toFixed(2)}</div>
                                    <div>Precio 2:</div><div className="text-right font-mono">${(p.precio_2 || 0).toFixed(2)}</div>
                                    <div>Precio 3:</div><div className="text-right font-mono">${(p.precio_3 || 0).toFixed(2)}</div>
                                    <div>Precio 4:</div><div className="text-right font-mono">${(p.precio_4 || 0).toFixed(2)}</div>
                                    <div>Precio 5:</div><div className="text-right font-mono">${(p.precio_5 || 0).toFixed(2)}</div>
                                    <div>Mayoreo:</div><div className="text-right font-mono">${(p.mayoreo || 0).toFixed(2)}</div>
                                    <div>Medio mayoreo:</div><div className="text-right font-mono">${(p.medio_mayoreo || 0).toFixed(2)}</div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs pt-2 border-t">
                                    <div>Stock:</div><div className="text-right font-mono">{p.stock || 0}</div>
                                    <div>Stock mín:</div><div className="text-right font-mono">{p.min_stock || 0}</div>
                                    <div>Comisión:</div><div className="text-right font-mono">${(p.default_commission || 0).toFixed(2)}</div>
                                  </div>
                                  {p.has_promo && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-0.5">
                                      <p className="font-semibold text-emerald-900">🏷️ Promoción activa</p>
                                      {p.promo_type === 'flat'
                                        ? <p>Precio fijo: <strong>${(p.promo_price || 0).toFixed(2)}</strong></p>
                                        : <p>${(p.promo_price || 0).toFixed(2)} si lleva ≥ <strong>{p.promo_min_qty || 0}</strong> pzas</p>
                                      }
                                      {p.promo_end_date && <p>Vence: {p.promo_end_date}</p>}
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
                        <td className="px-3 py-2">{cat?.name || '-'}</td>
                        <td className="px-3 py-2 text-right">${(p.precio_1 || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{stockBadge(p)}</td>
                        <td className="px-3 py-2">
                          {p.has_promo ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                              <Tag size={10} weight="fill" /> {promoLabel(p)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/products?edit=${p.id}&tab=promo`)}
                            data-testid={`promo-button-${p.id}`}
                            title="Configurar promoción (se abre en Productos)"
                            className={p.has_promo ? 'border-emerald-500 text-emerald-700' : ''}
                          >
                            <Tag size={14} weight={p.has_promo ? 'fill' : 'regular'} />
                            <span className="ml-1 hidden sm:inline">Promo</span>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <tr><td colSpan="7" className="px-3 py-8 text-center text-muted-foreground">Sin resultados</td></tr>
                  )}
                </tbody>
              </table>
              {filteredProducts.length > 500 && (
                <p className="p-2 text-xs text-center text-muted-foreground">Mostrando 500 de {filteredProducts.length}. Refina los filtros.</p>
              )}
            </div>
          </>
        ) : view === 'no-movement' ? (
          <div className="bg-white border border-border rounded-sm">
            <div className="px-3 py-2 border-b border-border bg-slate-50/40 flex items-end gap-3">
              <div>
                <Label className="text-xs">Sin movimiento en los últimos…</Label>
                <select
                  value={noMovementDays}
                  onChange={(e) => {
                    const d = parseInt(e.target.value, 10);
                    setNoMovementDays(d);
                    setNoMovementLoading(true);
                    axios.get(`${API}/products/no-movement?days=${d}`, { withCredentials: true })
                      .then(({ data }) => setNoMovement(data))
                      .catch(() => setNoMovement([]))
                      .finally(() => setNoMovementLoading(false));
                  }}
                  className="h-9 border rounded px-2 text-sm"
                  data-testid="no-movement-days"
                >
                  <option value="7">7 días</option>
                  <option value="15">15 días</option>
                  <option value="30">30 días</option>
                  <option value="60">60 días</option>
                  <option value="90">90 días</option>
                  <option value="180">6 meses</option>
                </select>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                {noMovementLoading ? 'Cargando…' : `${noMovement.length} productos sin movimiento`}
              </span>
            </div>
            <p className="px-3 py-1 text-[11px] text-muted-foreground bg-slate-50/70 border-b border-border">
              💡 Inventario muerto: productos activos con stock que NO se han vendido en el periodo seleccionado.
              Ordenado por &quot;nunca vendidos&quot; primero, luego por mayor tiempo sin venta.
            </p>
            <table className="w-full text-sm" data-testid="no-movement-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Producto</th>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-right">Stock</th>
                  <th className="px-3 py-2 text-right">Costo invertido</th>
                  <th className="px-3 py-2 text-left">Última venta</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {noMovement.map((p) => {
                  const invested = (p.cost || 0) * (p.stock || 0);
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">
                        {p.image_url && <img src={p.image_url} alt="" className="w-7 h-7 inline-block rounded object-cover mr-2" />}
                        {p.name}
                      </td>
                      <td className="px-3 py-2 text-xs">{p.sku || '-'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{p.stock}</td>
                      <td className="px-3 py-2 text-right text-rose-600">${invested.toFixed(2)}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.last_sale_at ? (
                          <span title={new Date(p.last_sale_at).toLocaleString('es-MX')}>
                            hace {p.days_since_last_sale} días
                          </span>
                        ) : (
                          <span className="text-rose-600 font-semibold">Nunca vendido</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!noMovementLoading && noMovement.length === 0 && (
                  <tr><td colSpan="5" className="px-3 py-8 text-center text-muted-foreground">
                    🎉 ¡Todos tus productos se están vendiendo en este periodo!
                  </td></tr>
                )}
              </tbody>
            </table>
            {noMovement.length > 0 && (
              <div className="px-3 py-2 border-t bg-slate-50/40 text-xs text-right">
                Costo total inmovilizado: <b className="text-rose-700">
                  ${noMovement.reduce((s, p) => s + ((p.cost || 0) * (p.stock || 0)), 0).toFixed(2)}
                </b>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-border rounded-sm">
            <div className="px-3 py-2 border-b border-border bg-slate-50/40 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Buscar producto o nota</Label>
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    value={movSearch}
                    onChange={(e) => setMovSearch(e.target.value)}
                    placeholder="Ej: silla, venta, ajuste…"
                    className="pl-8 h-9"
                    data-testid="movements-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={movFrom} onChange={(e) => setMovFrom(e.target.value)} className="h-9" data-testid="movements-from" />
              </div>
              <div>
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={movTo} onChange={(e) => setMovTo(e.target.value)} className="h-9" data-testid="movements-to" />
              </div>
              {(movSearch || movFrom || movTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setMovSearch(''); setMovFrom(''); setMovTo(''); }}>
                  Limpiar
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto">
                {filteredMovements.length} de {movements.length} movimientos
              </span>
            </div>
            <p className="px-3 py-1 text-[11px] text-muted-foreground bg-slate-50/70 border-b border-border">
              💡 Doble click sobre un movimiento de tipo &quot;salida&quot; para ver la venta relacionada.
            </p>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Producto</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Cantidad</th>
                  <th className="px-3 py-2 text-left">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredMovements.map((m) => {
                  const saleId = extractSaleId(m.notes);
                  return (
                  <tr
                    key={m.id}
                    className={`hover:bg-slate-50 ${saleId ? 'cursor-pointer' : ''}`}
                    onDoubleClick={() => saleId && openSaleDetail(saleId)}
                    title={saleId ? 'Doble click para ver la venta' : undefined}
                    data-testid={`movement-row-${m.id}`}
                  >
                    <td className="px-3 py-2 text-xs">{new Date(m.created_at).toLocaleString('es-MX')}</td>
                    <td className="px-3 py-2">{m.product_name}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        m.type === 'entrada' ? 'bg-emerald-100 text-emerald-700'
                          : m.type === 'salida' ? 'bg-rose-100 text-rose-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {m.type.charAt(0).toUpperCase() + m.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
                    <td className="px-3 py-2 text-xs">
                      {m.notes || '-'}
                      {saleId && <span className="ml-1 text-violet-600 text-[10px]">🔗</span>}
                    </td>
                  </tr>
                  );
                })}
                {filteredMovements.length === 0 && (
                  <tr><td colSpan="5" className="px-3 py-8 text-center text-muted-foreground">
                    {movements.length === 0 ? 'No hay movimientos' : 'Sin resultados para el filtro'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adjust dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajuste de Inventario</DialogTitle></DialogHeader>
          <form onSubmit={handleAdjust} className="space-y-4">
            <div>
              <Label>Producto *</Label>
              <Select value={adjustForm.product_id} onValueChange={(v) => setAdjustForm({ ...adjustForm, product_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{productNameWithVariant(p)} (Stock: {p.stock})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm({ ...adjustForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">Entrada</SelectItem>
                  <SelectItem value="salida">Salida</SelectItem>
                  <SelectItem value="ajuste">Ajuste</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad *</Label>
              <Input type="number" value={adjustForm.quantity} onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })} required />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={adjustForm.notes} onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })} />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancelar</Button>
              <Button type="submit">Registrar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Promo dialog */}

      {/* Sale detail dialog (abierto vía doble-click en movimiento) */}
      <Dialog
        open={saleDetail.open}
        onOpenChange={(o) => !o && setSaleDetail({ open: false, loading: false, sale: null, saleId: null })}
      >
        <DialogContent className="max-w-lg" data-testid="movement-sale-detail">
          <DialogHeader>
            <DialogTitle>Detalle de Venta</DialogTitle>
          </DialogHeader>
          {saleDetail.loading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : saleDetail.sale ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">ID:</span>
                <code className="text-[11px]">{saleDetail.sale.id?.slice(-8)}</code>
              </div>
              <div className="flex justify-between"><span>Fecha:</span><span>{saleDetail.sale.created_at ? new Date(saleDetail.sale.created_at).toLocaleString('es-MX') : '-'}</span></div>
              <div className="flex justify-between"><span>Vendedor:</span><span className="font-medium">{saleDetail.sale.seller_name || '—'}</span></div>
              <div className="flex justify-between"><span>Cliente:</span><span>{saleDetail.sale.client_name || '—'}</span></div>
              <div className="flex justify-between"><span>Método de pago:</span><span className="font-medium uppercase">{saleDetail.sale.payment_method || '—'}</span></div>
              {saleDetail.sale.status === 'cancelled' && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-800 text-xs">
                  ❌ Venta CANCELADA{saleDetail.sale.cancel_reason ? ` · ${saleDetail.sale.cancel_reason}` : ''}
                </div>
              )}
              <div className="border-t pt-2">
                <p className="text-xs font-semibold mb-1">Productos:</p>
                <ul className="space-y-1 text-xs">
                  {(saleDetail.sale.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{it.quantity}x {it.product_name}{it.variant_name ? ` (${it.variant_name})` : ''}</span>
                      <span className="font-mono">${(it.price * it.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total:</span>
                <span>${(saleDetail.sale.total || 0).toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-rose-600">No se pudo cargar la venta.</p>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
